import { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { SourceItem } from '@/hooks/useSuperStats';

interface TrafficSourcesProps {
  items: SourceItem[];
  loading: boolean;
}

// Palette issue des rôles StyleSystem (aucune couleur codée en dur).
const COLORS = [
  'oklch(var(--primary-500))',
  'oklch(var(--accent-500))',
  'oklch(var(--secondary-500))',
  'oklch(var(--primary-300))',
  'oklch(var(--accent-300))',
  'oklch(var(--secondary-300))',
  'oklch(var(--primary-700))',
  'oklch(var(--accent-700))',
];

/**
 * Répartition du trafic par source, sous forme de camembert cliquable.
 * Un clic sur une part (ou la légende) la met en avant et affiche son volume.
 */
export default function TrafficSources({ items, loading }: TrafficSourcesProps) {
  const [active, setActive] = useState<string | null>(null);
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const activeItem = items.find((item) => item.name === active) || null;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 h-full">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-1">
        <i className="ri-pie-chart-2-line text-accent-500"></i>
        Sources de trafic
      </h3>
      <p className="text-xs text-foreground-400 mb-3">
        {activeItem
          ? `${activeItem.name} — ${activeItem.value.toLocaleString('fr-FR')} visite(s)`
          : 'Cliquez une part pour voir le détail par provenance'}
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-foreground-400">
          <i className="ri-pie-chart-2-line text-3xl mb-2"></i>
          <p className="text-sm">Aucune source enregistrée</p>
        </div>
      ) : (
        <>
          <div className="relative h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={52}
                  outerRadius={82}
                  paddingAngle={2}
                  stroke="none"
                  onClick={(entry: { name?: string } | null) =>
                    setActive((prev) => (prev === entry?.name ? null : entry?.name ?? null))
                  }
                >
                  {items.map((item, index) => (
                    <Cell
                      key={item.name}
                      fill={COLORS[index % COLORS.length]}
                      opacity={active && active !== item.name ? 0.3 : 1}
                      className="cursor-pointer"
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'oklch(var(--background-50))',
                    border: '1px solid oklch(var(--foreground-200) / 0.5)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: 'oklch(var(--foreground-900))',
                  }}
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString('fr-FR')} visite(s)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xl font-bold font-heading text-foreground-950">
                {total.toLocaleString('fr-FR')}
              </span>
              <span className="text-[10px] text-foreground-400 uppercase tracking-wide">
                visites
              </span>
            </div>
          </div>

          <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto pr-1">
            {items.map((item, index) => {
              const isActive = active === item.name;
              const pct = total > 0 ? (item.value / total) * 100 : 0;
              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => setActive((prev) => (prev === item.name ? null : item.name))}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-left transition-colors cursor-pointer ${
                    isActive ? 'bg-background-100' : 'hover:bg-background-100/70'
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></span>
                  <span className="flex-1 min-w-0 text-xs text-foreground-800 truncate">
                    {item.name}
                  </span>
                  <span className="text-xs font-semibold text-foreground-900 whitespace-nowrap">
                    {item.value.toLocaleString('fr-FR')}
                  </span>
                  <span className="text-[10px] text-foreground-400 whitespace-nowrap w-10 text-right">
                    {pct.toFixed(0)}%
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}